import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FloppyDisk, Trash, Bell, BellSlash, ArrowClockwise, X } from "@phosphor-icons/react";

/**
 * Recruiter-side Saved Searches for the Talent Marketplace.
 *
 * Props:
 *   - currentFilters: { q, location, min_experience, max_experience, verified_only, ...}
 *       — snapshot of the marketplace filter state to save.
 *   - onApply({ q, location, min_experience, ... }): callback fired when a
 *       saved search is loaded — parent updates its filter state.
 */
export default function SavedSearchesPanel({ currentFilters, onApply }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [saveName, setSaveName] = useState("");
    const [freq, setFreq] = useState("daily");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/marketplace/saved-searches");
            setItems(r.data.items || []);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filterKeys = Object.keys(currentFilters || {}).filter(k => currentFilters[k] !== "" && currentFilters[k] !== false && currentFilters[k] != null);
    const canSave = filterKeys.length > 0;

    const doSave = async () => {
        const name = saveName.trim();
        if (!name) return toast.error("Give this search a name");
        if (!canSave) return toast.error("Set at least one filter first");
        setSaving(true);
        try {
            await api.post("/marketplace/saved-searches", {
                name, filters: currentFilters, notify_frequency: freq,
            });
            toast.success(`Saved · ${freq === "off" ? "no alerts" : `${freq} email alerts`}`);
            setSaveName("");
            setOpen(false);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't save");
        } finally { setSaving(false); }
    };

    const doDelete = async (id) => {
        if (!window.confirm("Delete this saved search?")) return;
        try {
            await api.delete(`/marketplace/saved-searches/${id}`);
            toast.success("Deleted");
            load();
        } catch { toast.error("Couldn't delete"); }
    };

    const toggleFreq = async (row) => {
        const next = row.notify_frequency === "off" ? "daily" : "off";
        try {
            await api.patch(`/marketplace/saved-searches/${row.id}`, { notify_frequency: next });
            toast.success(next === "off" ? "Alerts paused" : "Daily alerts on");
            load();
        } catch { toast.error("Couldn't update"); }
    };

    const summariseFilters = (f) => {
        const chips = [];
        if (f.q) chips.push(`"${f.q}"`);
        if (f.skills) chips.push(`skills: ${f.skills}`);
        if (f.location) chips.push(f.location);
        if (f.min_experience != null) chips.push(`${f.min_experience}+ yrs`);
        if (f.verified_only) chips.push("Verified");
        if (f.pro_only) chips.push("Pro");
        if (f.featured_only) chips.push("Featured");
        if (f.prep_ready_only) chips.push("Interview-ready");
        return chips.slice(0, 5).join(" · ") || "no filters";
    };

    return (
        <div data-testid="saved-searches-panel"
             className="bg-white border border-[#E5E5E5] p-3 sm:p-4 mb-6">
            <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <FloppyDisk size={14} weight="bold" className="text-cobalt" />
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-ink">Saved searches</p>
                    <span className="text-[10px] text-[#A3A3A3]">
                        {loading ? "…" : `${items.length} of 10`}
                    </span>
                </div>
                <button onClick={() => setOpen((v) => !v)}
                        disabled={!canSave}
                        data-testid="save-search-toggle-btn"
                        className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border-2 border-ink bg-white hover:bg-ink hover:text-white disabled:opacity-40 disabled:hover:bg-white disabled:hover:text-ink transition-colors inline-flex items-center gap-1">
                    <FloppyDisk size={11} weight="bold" /> Save this search
                </button>
            </div>

            {open && (
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 mb-3" data-testid="save-search-form">
                    <div className="flex flex-wrap items-center gap-2">
                        <input
                            data-testid="save-search-name"
                            value={saveName}
                            onChange={(e) => setSaveName(e.target.value)}
                            placeholder="Name this search (e.g. Python 5+ Bengaluru)"
                            className="flex-1 min-w-[240px] px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                            maxLength={80}
                        />
                        <select
                            data-testid="save-search-freq"
                            value={freq}
                            onChange={(e) => setFreq(e.target.value)}
                            className="px-3 py-2 text-sm border border-[#E5E5E5] focus:outline-none focus:border-ink"
                        >
                            <option value="daily">Daily email</option>
                            <option value="weekly">Weekly email</option>
                            <option value="off">No email</option>
                        </select>
                        <button onClick={doSave} disabled={saving || !saveName.trim()}
                                data-testid="save-search-confirm-btn"
                                className="text-[10px] font-black uppercase tracking-wider px-4 py-2 bg-ink text-white disabled:opacity-50">
                            {saving ? "Saving…" : "Save"}
                        </button>
                        <button onClick={() => setOpen(false)}
                                className="p-2 text-[#A3A3A3] hover:text-ink"
                                data-testid="save-search-cancel-btn">
                            <X size={14} weight="bold" />
                        </button>
                    </div>
                    <p className="text-[10px] text-[#A3A3A3] mt-2">
                        Daily/weekly digests email you newly matching candidates. Cap: 10 searches per user.
                    </p>
                </div>
            )}

            {loading ? (
                <p className="text-xs text-[#A3A3A3]">Loading…</p>
            ) : items.length === 0 ? (
                <p className="text-xs text-[#A3A3A3]">
                    No saved searches yet. Set some filters and hit <b>Save this search</b> to get daily email alerts for new matching candidates.
                </p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {items.map((s) => (
                        <div key={s.id} data-testid={`saved-search-item-${s.id}`}
                             className="group inline-flex items-center gap-1 bg-[#FAFAFA] border border-[#E5E5E5] hover:border-cobalt pl-3 pr-1 py-1.5">
                            <button onClick={() => onApply?.(s.filters, s)}
                                    data-testid={`saved-search-apply-${s.id}`}
                                    className="flex flex-col items-start text-left mr-2"
                                    title={summariseFilters(s.filters)}>
                                <span className="text-xs font-black text-ink leading-tight">{s.name}</span>
                                <span className="text-[9px] text-[#A3A3A3] leading-tight max-w-[220px] truncate">{summariseFilters(s.filters)}</span>
                            </button>
                            <button onClick={() => toggleFreq(s)} title={s.notify_frequency === "off" ? "Alerts paused" : `${s.notify_frequency} alerts on`}
                                    data-testid={`saved-search-toggle-${s.id}`}
                                    className={`p-1.5 ${s.notify_frequency === "off" ? "text-[#A3A3A3]" : "text-cobalt"} hover:bg-white`}>
                                {s.notify_frequency === "off"
                                    ? <BellSlash size={12} weight="bold" />
                                    : <Bell size={12} weight="fill" />}
                            </button>
                            <button onClick={() => doDelete(s.id)} title="Delete"
                                    data-testid={`saved-search-delete-${s.id}`}
                                    className="p-1.5 text-[#A3A3A3] hover:text-red-500 hover:bg-white">
                                <Trash size={12} weight="bold" />
                            </button>
                        </div>
                    ))}
                    <button onClick={load} title="Refresh"
                            className="inline-flex items-center gap-1 px-2 py-1.5 text-[10px] text-[#A3A3A3] hover:text-ink">
                        <ArrowClockwise size={11} weight="bold" />
                    </button>
                </div>
            )}
        </div>
    );
}
